'use client';

/**
 * SearchBarSection — schema-driven explorer search input.
 *
 * Generic input + button + hint, wired to
 * `<ExplorerSearchProvider />` for state. Submitting (Enter or
 * button click) calls `context.submit()` — the provider decides
 * what that means: obeya simulations / agents fire `ki_ask`,
 * future filter-style modes can wire a debounced filter update.
 *
 * Without a provider the input is disabled — schemas declaring
 * a SEARCH_BAR section are signalling they want one wired, so a
 * disabled input is a more useful affordance than a silent
 * locally-controlled input that goes nowhere.
 *
 * Renders any error from the context underneath the input
 * — same treatment the legacy obeya simulations panel did.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.SEARCH_BAR
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { Loader2, Play, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';
import { useExplorerSearch } from './explorer-search-context';

interface SearchBarSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function SearchBarSection({ config, labels }: SearchBarSectionProps) {
  const search = useExplorerSearch();

  const placeholder =
    (config.placeholder && labels[config.placeholder])
    ?? config.placeholder
    ?? labels.search_placeholder
    ?? 'Search...';
  const hint =
    (config.help_text && labels[config.help_text])
    ?? config.help_text
    ?? null;
  const submitLabel = labels.ask ?? 'Ask';

  const disabled = !search;
  const query = search?.query ?? '';
  const isLoading = search?.isLoading ?? false;
  const error = search?.error ?? null;

  const onSubmit = () => {
    if (!search || !query.trim() || isLoading) return;
    void search.submit();
  };

  return (
    <div className="p-3 border-b border-border">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={query}
            onChange={(e) => search?.setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSubmit();
            }}
            disabled={disabled}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Button
          size="sm"
          onClick={onSubmit}
          disabled={disabled || !query.trim() || isLoading}
          className="h-9"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Play className="w-4 h-4 mr-1" />
              {submitLabel}
            </>
          )}
        </Button>
      </div>
      {hint && (
        <p className="text-xs text-muted-foreground mt-2">{hint}</p>
      )}
      {error && (
        <p className="text-xs text-destructive mt-2">{error}</p>
      )}
    </div>
  );
}
